// h5ad-viewer\src\App.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import H5ADUploader from "./components/H5ADUploader";
import H5ADMetadataViewer from "./components/H5ADMetadataViewer";
import H5ADReadPanel from "./components/H5ADReadPanel";
import SavedFileSelector from "./components/SavedFileSelector";
import ExpressionHeatmap from "./components/ExpressionHeatmap";
import CompareTab from "./pages/CompareTab";

const API_BASE = process.env.REACT_APP_API_BASE;

function App() {
  const navigate = useNavigate();
  const [handle, setHandle] = useState(null);
  const [tab, setTab] = useState("metadata");
  const [resetSelectorCounter, setResetSelectorCounter] = useState(0);
  const [metadataCache, setMetadataCache] = useState({});
  const [metadataStatus, setMetadataStatus] = useState("idle");
  const [readResult, setReadResult] = useState(null);
  const [readStatus, setReadStatus] = useState("idle");
  const [readElapsedTime, setReadElapsedTime] = useState(0);
  const [readDuration, setReadDuration] = useState(null);

  useEffect(() => {
    if (readStatus !== "requesting") {
      setReadElapsedTime(0);
      return;
    }

    const start = Date.now();

    const interval = setInterval(() => {
      const elapsedSec = (Date.now() - start) / 1000;
      setReadElapsedTime(elapsedSec.toFixed(1));
    }, 100);

    return () => clearInterval(interval);
  }, [readStatus]);

  // Fetch and cache metadata when handle changes
  useEffect(() => {
    if (!handle) return;

    const isCached = metadataCache[handle];
    if (isCached) {
      console.log(`📦 Using cached metadata for "${handle}"`);
      setMetadataStatus("idle");
      return;
    }

    const fetchMetadata = async () => {
      console.log(`🌐 Requesting metadata for handle "${handle}"`);
      setMetadataStatus("requesting");

      const start = performance.now();

      try {
        console.log("📡 API base:", API_BASE);
        const res = await fetch(`${API_BASE}/metadata/${handle}`);
        const data = await res.json();

        const sizeKB = JSON.stringify(data).length / 1024;
        console.log(`📥 Received metadata: ${sizeKB.toFixed(2)} KB for "${handle}"`);

        setMetadataStatus("saving");
        setMetadataCache(prev => ({ ...prev, [handle]: data }));
        console.log(`✅ Metadata saved for "${handle}" in ${((performance.now() - start) / 1000).toFixed(2)}s`);
      } catch (err) {
        console.error(`❌ Metadata fetch failed for "${handle}":`, err);
        setMetadataCache(prev => ({ ...prev, [handle]: { error: err.message } }));
      } finally {
        setTimeout(() => {
          setMetadataStatus("idle");
          console.log(`🔽 Metadata status set to idle for "${handle}"`);
        }, 0);
      }
    };

    fetchMetadata();
  }, [handle]);

  const resetQueryState = () => {
    setReadResult(null);
    setReadStatus("idle");
    setReadDuration(null);
  };

  const handleUploadSuccess = (newHandle) => {
    console.log(`📤 File uploaded successfully: handle = "${newHandle}"`);
    setHandle(newHandle);
    setTab("metadata");
    setMetadataCache({}); // 🔁 Clear cache when a new file is uploaded
    setResetSelectorCounter(c => c + 1);

    // 🧹 Clean up previous query results
    resetQueryState();
  };

  const handleFileSelect = (filename) => {
    const newHandle = filename.replace(/\.h5ad$/, "");
    console.log(`📂 File selected: "${filename}", parsed handle = "${newHandle}"`);
    setHandle(newHandle);
    setTab("metadata");

    // 🧹 Clean up previous query results
    resetQueryState();
  };

  const handleTabSwitch = (targetTab) => {
    console.log(`🗂 Switching tab to: "${targetTab}"`);
    setTab(targetTab);

    if (targetTab === "metadata") {
      console.log("⚡ Showing loading page for metadata (from cache or network)");
      setMetadataStatus("loading");

      // Wait a tick to render loading screen before heavy metadata display
      setTimeout(() => {
        const cached = metadataCache[handle];
        if (cached) {
          console.log(`🧠 Using cached metadata for "${handle}"`);
          setMetadataStatus("idle");
        }
      }, 0);
    }
  };

  const metadata = handle ? metadataCache[handle] : null;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <h1 className="text-2xl font-bold mb-4">🧬 H5AD Viewer</h1>

      <div className="mt-4 flex space-x-2 mb-4">
        <button
          className={`px-3 py-1 rounded ${tab === "metadata" ? "bg-blue-600 text-white" : "bg-white text-blue-600 border"}`}
          onClick={() => handleTabSwitch("metadata")}
        >
          Metadata
        </button>
        <button
          className={`px-3 py-1 rounded ${tab === "query" ? "bg-blue-600 text-white" : "bg-white text-blue-600 border"}`}
          onClick={() => handleTabSwitch("query")}
        >
          Query
        </button>
        <button
          className={`px-3 py-1 rounded ${tab === "compare" ? "bg-blue-600 text-white" : "bg-white text-blue-600 border"}`}
          onClick={() => setTab("compare")}
        >
          Compare
        </button>
      </div>

      <div className="flex">
        {/* Left Panel */}
        {tab !== "compare" ? (
          <div className="w-1/4 pr-4 space-y-4">
            <H5ADUploader onUploadSuccess={handleUploadSuccess} />
            <SavedFileSelector onSelect={handleFileSelect} resetTrigger={resetSelectorCounter} />
            {handle && tab === "query" && (
              <H5ADReadPanel
                handle={handle}
                metadata={metadata}
                onResult={setReadResult}
                setReadStatus={setReadStatus}
                setReadDuration={setReadDuration}
              />
            )}
          </div>
        ) : null}

        {/* Right Panel (or full-width for Compare) */}
        {tab === "compare" ? (
          <div className="w-full">
            <CompareTab />
          </div>
        ) : (
          <div className="w-3/4 bg-white rounded shadow p-4">
            {handle && tab === "metadata" && (
              <H5ADMetadataViewer
                handle={handle}
                metadata={metadata}
                status={metadataStatus}
              />
            )}
            {handle && tab === "query" && (
              <>
                {readStatus !== "idle" ? (
                  <div className="text-gray-600 italic">
                    {readStatus === "requesting" && `🌐 Requesting expression matrix... (${readElapsedTime}s)`}
                    {readStatus === "error" && "❌ Failed to load expression matrix."}
                  </div>
                ) : (
                  <>
                    {readDuration && (
                      <div className="text-green-600 font-medium mb-2">
                        ✅ Loaded in {readDuration}s
                      </div>
                    )}
                    <ExpressionHeatmap
                      cells={readResult?.cells}
                      genes={readResult?.genes}
                      matrix={readResult?.X}
                    />
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
